import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Button,
    Flex,
    Grid,
    IconButton,
    Input,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    UnorderedList
} from "@chakra-ui/react";
import {BiTrash} from "react-icons/bi";
import {v4 as uuidv4} from "uuid";
import BeaconAddressInput from "../../components/form/BeaconAddressInput";
import {BeaconTrigger, WiFiApTrigger} from "../../components/types";
import {CueTriggerSet} from "./cueTriggerHelpers";
import {HELP_TOOLTIP_DELAY_MS} from "../../components/help/helpConfig";
import {isValidCueBeaconMac, normalizeCueBeaconTrigger, normalizeCueWiFiTrigger} from "./cueTriggerDialogUtils";

type SpotTriggerContext = {
    spotId: string;
    spotName: string;
    beaconTriggers: BeaconTrigger[];
    wiFiTriggers: WiFiApTrigger[];
};

type MultiBeaconEntry = {
    entryId: string;
    macAddr: string;
    majorId: string;
    minorId: string;
    uuid?: string;
    note?: string;
    ownerSpotIds: string[];
    sourceIdsBySpotId: Record<string, string | undefined>;
    canExcludeCommon: boolean;
};

type MultiWiFiEntry = {
    entryId: string;
    name: string;
    ownerSpotIds: string[];
    sourceIdsBySpotId: Record<string, string | undefined>;
    canExcludeCommon: boolean;
};

const beaconKey = (trigger: BeaconTrigger) => {
    const macAddr = (trigger.macAddr || "").trim().toUpperCase();
    const majorId = (trigger.majorId || "").trim();
    const minorId = (trigger.minorId || "").trim();
    const uuid = (trigger.uuid || "").trim().toUpperCase();
    const note = (trigger.note || "").trim();
    return `${macAddr}|${majorId}|${minorId}|${uuid}|${note}`;
};

const wifiKey = (trigger: WiFiApTrigger) => {
    return (trigger.name || "").trim();
};

const buildInitialBeaconEntries = (spotContexts: SpotTriggerContext[]) => {
    const grouped = new Map<string, MultiBeaconEntry>();
    spotContexts.forEach((spot) => {
        (spot.beaconTriggers || []).forEach((trigger) => {
            const key = beaconKey(trigger);
            if (!key) {
                return;
            }
            const existing = grouped.get(key);
            if (existing) {
                if (!existing.ownerSpotIds.includes(spot.spotId)) {
                    existing.ownerSpotIds.push(spot.spotId);
                }
                existing.sourceIdsBySpotId[spot.spotId] = trigger.id;
                return;
            }
            grouped.set(key, {
                entryId: uuidv4(),
                macAddr: (trigger.macAddr || "").trim().toUpperCase(),
                majorId: trigger.majorId || "",
                minorId: trigger.minorId || "",
                uuid: (trigger.uuid || "").trim().toUpperCase() || undefined,
                note: (trigger.note || "").trim() || undefined,
                ownerSpotIds: [spot.spotId],
                sourceIdsBySpotId: {
                    [spot.spotId]: trigger.id
                },
                canExcludeCommon: false
            });
        });
    });
    return Array.from(grouped.values());
};

const buildInitialWiFiEntries = (spotContexts: SpotTriggerContext[]) => {
    const grouped = new Map<string, MultiWiFiEntry>();
    spotContexts.forEach((spot) => {
        (spot.wiFiTriggers || []).forEach((trigger) => {
            const key = wifiKey(trigger);
            if (!key) {
                return;
            }
            const existing = grouped.get(key);
            if (existing) {
                if (!existing.ownerSpotIds.includes(spot.spotId)) {
                    existing.ownerSpotIds.push(spot.spotId);
                }
                existing.sourceIdsBySpotId[spot.spotId] = trigger.id;
                return;
            }
            grouped.set(key, {
                entryId: uuidv4(),
                name: (trigger.name || "").trim(),
                ownerSpotIds: [spot.spotId],
                sourceIdsBySpotId: {
                    [spot.spotId]: trigger.id
                },
                canExcludeCommon: false
            });
        });
    });
    return Array.from(grouped.values());
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    spotContexts: SpotTriggerContext[];
    onSave: (updates: Array<{spotId: string; triggerSet: CueTriggerSet}>) => void;
    canEdit: boolean;
    allowBeaconTriggers?: boolean;
};

const CueMultiSpotTriggerEditorDialog = ({
    isOpen,
    onClose,
    spotContexts,
    onSave,
    canEdit,
    allowBeaconTriggers = true
}: Props) => {
    const spotIds = useMemo(() => spotContexts.map((spot) => spot.spotId), [spotContexts]);
    const spotNameById = useMemo(() => {
        const map = new Map<string, string>();
        spotContexts.forEach((spot) => {
            map.set(spot.spotId, spot.spotName || spot.spotId);
        });
        return map;
    }, [spotContexts]);
    const [draftBeaconEntries, setDraftBeaconEntries] = useState<MultiBeaconEntry[]>([]);
    const [draftWiFiEntries, setDraftWiFiEntries] = useState<MultiWiFiEntry[]>([]);
    const usageTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const pendingUsageTooltipRef = useRef<{
        key: string;
        content: ReactNode;
        x: number;
        y: number;
    } | undefined>(undefined);
    const [activeUsageTooltip, setActiveUsageTooltip] = useState<{
        key: string;
        content: ReactNode;
        x: number;
        y: number;
    } | undefined>(undefined);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDraftBeaconEntries(buildInitialBeaconEntries(spotContexts));
        setDraftWiFiEntries(buildInitialWiFiEntries(spotContexts));
    }, [isOpen, spotContexts]);

    useEffect(() => {
        return () => {
            if (usageTooltipTimerRef.current) {
                clearTimeout(usageTooltipTimerRef.current);
            }
        };
    }, []);

    const hasInvalidBeacon = useMemo(() => {
        if (!allowBeaconTriggers) {
            return false;
        }
        return draftBeaconEntries.some((entry) => {
            const value = (entry.macAddr || "").trim();
            if (!value) {
                return false;
            }
            return !isValidCueBeaconMac(value);
        });
    }, [allowBeaconTriggers, draftBeaconEntries]);

    const sortByCommonFirst = useCallback(<T extends {ownerSpotIds: string[]}>(
        items: T[],
        getLabel: (item: T) => string
    ) => {
        return [...items].sort((a, b) => {
            const aCommon = a.ownerSpotIds.length === spotIds.length;
            const bCommon = b.ownerSpotIds.length === spotIds.length;
            if (aCommon !== bCommon) {
                return aCommon ? -1 : 1;
            }
            return getLabel(a).localeCompare(getLabel(b));
        });
    }, [spotIds.length]);

    const sortedBeaconEntries = useMemo(() => {
        return sortByCommonFirst(draftBeaconEntries, (entry) => `${entry.macAddr} ${entry.uuid || ""} ${entry.note || ""}`);
    }, [draftBeaconEntries, sortByCommonFirst]);

    const sortedWiFiEntries = useMemo(() => {
        return sortByCommonFirst(draftWiFiEntries, (entry) => entry.name || "");
    }, [draftWiFiEntries, sortByCommonFirst]);

    const getUsageHoverText = (ownerSpotIds: string[]) => {
        const names = ownerSpotIds
            .map((spotId) => spotNameById.get(spotId) || spotId)
            .filter(Boolean);
        if (names.length === 0) {
            return undefined;
        }
        return (
            <Box>
                <Text fontWeight={"semibold"} mb={2}>Used in:</Text>
                <UnorderedList marginStart={5} spacing={1}>
                    {names.map((name) => (
                        <ListItem key={name}>
                            <Text fontSize={"sm"}>{name}</Text>
                        </ListItem>
                    ))}
                </UnorderedList>
            </Box>
        );
    };

    const clearUsageTooltipTimer = () => {
        if (usageTooltipTimerRef.current) {
            clearTimeout(usageTooltipTimerRef.current);
            usageTooltipTimerRef.current = undefined;
        }
    };

    const isTooltipExemptTarget = (target: EventTarget | null) => {
        if (!(target instanceof Element)) {
            return false;
        }
        return !!target.closest("[data-usage-tooltip-exempt=\"true\"]");
    };

    const onUsageHoverEnter = (key: string, content: ReactNode | undefined, event: React.MouseEvent) => {
        if (!content) {
            return;
        }
        if (isTooltipExemptTarget(event.target)) {
            return;
        }
        clearUsageTooltipTimer();
        pendingUsageTooltipRef.current = {
            key,
            content,
            x: event.clientX,
            y: event.clientY
        };
        usageTooltipTimerRef.current = setTimeout(() => {
            const pending = pendingUsageTooltipRef.current;
            if (!pending || pending.key !== key) {
                return;
            }
            setActiveUsageTooltip(pending);
            usageTooltipTimerRef.current = undefined;
        }, HELP_TOOLTIP_DELAY_MS);
    };

    const onUsageHoverMove = (key: string, event: React.MouseEvent) => {
        if (isTooltipExemptTarget(event.target)) {
            onUsageHoverLeave(key);
            return;
        }
        const pending = pendingUsageTooltipRef.current;
        if (!pending || pending.key !== key) {
            return;
        }
        pendingUsageTooltipRef.current = {
            ...pending,
            x: event.clientX,
            y: event.clientY
        };
    };

    const onUsageHoverLeave = (key: string) => {
        const pending = pendingUsageTooltipRef.current;
        if (pending?.key === key) {
            pendingUsageTooltipRef.current = undefined;
        }
        clearUsageTooltipTimer();
        setActiveUsageTooltip((previous) => previous?.key === key ? undefined : previous);
    };

    const usageTooltipPosition = useMemo(() => {
        if (!activeUsageTooltip) {
            return undefined;
        }
        const left = activeUsageTooltip.x + 12;
        const top = Math.max(8, activeUsageTooltip.y - 14);
        const arrowTop = Math.max(6, activeUsageTooltip.y - top - 6);
        return {left, top, arrowTop};
    }, [activeUsageTooltip]);

    const toggleCommonForBeacon = (entryId: string) => {
        setDraftBeaconEntries((previous) => previous.map((entry) => {
            if (entry.entryId !== entryId) {
                return entry;
            }
            const isCommon = entry.ownerSpotIds.length === spotIds.length;
            if (isCommon) {
                if (!entry.canExcludeCommon) {
                    return entry;
                }
                const keepSpotId = spotIds.find((spotId) => entry.ownerSpotIds.includes(spotId));
                return {
                    ...entry,
                    ownerSpotIds: keepSpotId ? [keepSpotId] : [],
                    canExcludeCommon: false
                };
            }
            return {
                ...entry,
                ownerSpotIds: [...spotIds],
                canExcludeCommon: true
            };
        }).filter((entry) => entry.ownerSpotIds.length > 0));
    };

    const toggleCommonForWiFi = (entryId: string) => {
        setDraftWiFiEntries((previous) => previous.map((entry) => {
            if (entry.entryId !== entryId) {
                return entry;
            }
            const isCommon = entry.ownerSpotIds.length === spotIds.length;
            if (isCommon) {
                if (!entry.canExcludeCommon) {
                    return entry;
                }
                const keepSpotId = spotIds.find((spotId) => entry.ownerSpotIds.includes(spotId));
                return {
                    ...entry,
                    ownerSpotIds: keepSpotId ? [keepSpotId] : [],
                    canExcludeCommon: false
                };
            }
            return {
                ...entry,
                ownerSpotIds: [...spotIds],
                canExcludeCommon: true
            };
        }).filter((entry) => entry.ownerSpotIds.length > 0));
    };

    const addBeacon = () => {
        setDraftBeaconEntries((previous) => ([
            ...previous,
            {
                entryId: uuidv4(),
                macAddr: "",
                majorId: "",
                minorId: "",
                ownerSpotIds: [...spotIds],
                sourceIdsBySpotId: {},
                canExcludeCommon: false
            }
        ]));
    };

    const addWiFi = () => {
        setDraftWiFiEntries((previous) => ([
            ...previous,
            {
                entryId: uuidv4(),
                name: "",
                ownerSpotIds: [...spotIds],
                sourceIdsBySpotId: {},
                canExcludeCommon: false
            }
        ]));
    };

    const save = () => {
        const updates = spotContexts.map((spot) => {
            const beaconTriggers = allowBeaconTriggers
                ? draftBeaconEntries
                    .filter((entry) => entry.ownerSpotIds.includes(spot.spotId))
                    .map((entry) => normalizeCueBeaconTrigger({
                        id: entry.sourceIdsBySpotId[spot.spotId] || uuidv4(),
                        type: "Beacon",
                        macAddr: entry.macAddr,
                        majorId: entry.majorId || "",
                        minorId: entry.minorId || "",
                        uuid: entry.uuid,
                        note: entry.note
                    }))
                    .filter((trigger): trigger is BeaconTrigger => !!trigger)
                : (spot.beaconTriggers || []).map((trigger) => ({...trigger}));
            const wiFiTriggers = draftWiFiEntries
                .filter((entry) => entry.ownerSpotIds.includes(spot.spotId))
                .map((entry) => normalizeCueWiFiTrigger({
                    id: entry.sourceIdsBySpotId[spot.spotId] || uuidv4(),
                    type: "WiFiAP",
                    name: entry.name
                }))
                .filter((trigger): trigger is WiFiApTrigger => !!trigger);
            return {
                spotId: spot.spotId,
                triggerSet: {
                    beaconTriggers,
                    wiFiTriggers
                }
            };
        });
        onSave(updates);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={"3xl"}>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{`Spot triggers - ${spotContexts.length} selected`}</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Text fontSize={"sm"} color={"gray.600"} mb={3}>
                        Common triggers are shown first. Use Include/Exclude to add or remove a trigger from the common set.
                    </Text>
                    {allowBeaconTriggers ? (
                        <>
                            <Flex justify={"space-between"} align={"center"} mb={3}>
                                <Text fontWeight={"semibold"}>Beacon triggers</Text>
                                <Button size={"sm"} onClick={addBeacon} isDisabled={!canEdit}>Add Beacon</Button>
                            </Flex>
                            <Box>
                                {sortedBeaconEntries.length === 0 ? (
                                    <Text fontSize={"sm"} color={"gray.500"} mb={3}>No beacon triggers</Text>
                                ) : sortedBeaconEntries.map((entry, index) => {
                                    const isCommon = entry.ownerSpotIds.length === spotIds.length;
                                    const usageHover = isCommon ? undefined : getUsageHoverText(entry.ownerSpotIds);
                                    return (
                                        <Grid
                                            key={`beacon-multi-${entry.entryId}`}
                                            templateColumns={"max-content 150px max-content 140px max-content 1fr 82px 36px"}
                                            columnGap={3}
                                            rowGap={2}
                                            mb={3}
                                            alignItems={"center"}
                                            opacity={isCommon ? 1 : 0.72}
                                            bg={isCommon ? "white" : "gray.50"}
                                            borderRadius={"md"}
                                            px={2}
                                            py={2}
                                            onMouseEnter={(event) => onUsageHoverEnter(`beacon-${entry.entryId}`, usageHover, event)}
                                            onMouseMove={(event) => onUsageHoverMove(`beacon-${entry.entryId}`, event)}
                                            onMouseLeave={() => onUsageHoverLeave(`beacon-${entry.entryId}`)}
                                        >
                                            <Text fontSize={"sm"} color={"gray.600"}>Address</Text>
                                            <BeaconAddressInput
                                                beaconAddress={entry.macAddr || ""}
                                                index={index}
                                                onBeaconAddressChange={(value, idx) => {
                                                    const row = sortedBeaconEntries[idx];
                                                    if (!row) {
                                                        return;
                                                    }
                                                    setDraftBeaconEntries((previous) => previous.map((item) => item.entryId === row.entryId ? {
                                                        ...item,
                                                        macAddr: value
                                                    } : item));
                                                }}
                                            />
                                            <Text fontSize={"sm"} color={"gray.600"}>UUID</Text>
                                            <Input
                                                size={"sm"}
                                                value={entry.uuid || ""}
                                                placeholder={"e.g. A1B2"}
                                                onChange={(event) => {
                                                    const value = event.target.value.trim().toUpperCase();
                                                    setDraftBeaconEntries((previous) => previous.map((item) => item.entryId === entry.entryId ? {
                                                        ...item,
                                                        uuid: value.length > 0 ? value : undefined
                                                    } : item));
                                                }}
                                                isDisabled={!canEdit}
                                            />
                                            <Text fontSize={"sm"} color={"gray.600"}>Notes</Text>
                                            <Input
                                                size={"sm"}
                                                value={entry.note || ""}
                                                placeholder={"Optional notes"}
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    setDraftBeaconEntries((previous) => previous.map((item) => item.entryId === entry.entryId ? {
                                                        ...item,
                                                        note: value
                                                    } : item));
                                                }}
                                                isDisabled={!canEdit}
                                            />
                                            {!isCommon || entry.canExcludeCommon ? (
                                                <Button
                                                    size={"xs"}
                                                    variant={"outline"}
                                                    onClick={() => toggleCommonForBeacon(entry.entryId)}
                                                    isDisabled={!canEdit}
                                                    title={isCommon ? "Exclude from common trigger set" : "Include in common trigger set"}
                                                    data-usage-tooltip-exempt={"true"}
                                                >
                                                    {isCommon ? "Exclude" : "Include"}
                                                </Button>
                                            ) : <Box/>}
                                            <IconButton
                                                aria-label={"Delete beacon trigger"}
                                                icon={<BiTrash/>}
                                                size={"sm"}
                                                variant={"ghost"}
                                                onClick={() => {
                                                    setDraftBeaconEntries((previous) => previous.filter((item) => item.entryId !== entry.entryId));
                                                }}
                                                isDisabled={!canEdit}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Box>
                        </>
                    ) : null}

                    <Flex justify={"space-between"} align={"center"} mt={4} mb={3}>
                        <Text fontWeight={"semibold"}>WiFi AP triggers</Text>
                        <Button size={"sm"} onClick={addWiFi} isDisabled={!canEdit}>Add WiFi AP</Button>
                    </Flex>
                    <Box>
                        {sortedWiFiEntries.length === 0 ? (
                            <Text fontSize={"sm"} color={"gray.500"}>No WiFi AP triggers</Text>
                        ) : sortedWiFiEntries.map((entry) => {
                            const isCommon = entry.ownerSpotIds.length === spotIds.length;
                            const usageHover = isCommon ? undefined : getUsageHoverText(entry.ownerSpotIds);
                            return (
                                <Grid
                                    key={`wifi-multi-${entry.entryId}`}
                                    templateColumns={"1fr 82px 36px"}
                                    columnGap={2}
                                    rowGap={2}
                                    mb={2}
                                    alignItems={"center"}
                                    opacity={isCommon ? 1 : 0.72}
                                    bg={isCommon ? "white" : "gray.50"}
                                    borderRadius={"md"}
                                    px={2}
                                    py={2}
                                    onMouseEnter={(event) => onUsageHoverEnter(`wifi-${entry.entryId}`, usageHover, event)}
                                    onMouseMove={(event) => onUsageHoverMove(`wifi-${entry.entryId}`, event)}
                                    onMouseLeave={() => onUsageHoverLeave(`wifi-${entry.entryId}`)}
                                >
                                    <Input
                                        size={"sm"}
                                        value={entry.name || ""}
                                        placeholder={"WiFi network name"}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setDraftWiFiEntries((previous) => previous.map((item) => item.entryId === entry.entryId ? {
                                                ...item,
                                                name: value
                                            } : item));
                                        }}
                                        isDisabled={!canEdit}
                                    />
                                    {!isCommon || entry.canExcludeCommon ? (
                                        <Button
                                            size={"xs"}
                                            variant={"outline"}
                                            onClick={() => toggleCommonForWiFi(entry.entryId)}
                                            isDisabled={!canEdit}
                                            title={isCommon ? "Exclude from common trigger set" : "Include in common trigger set"}
                                            data-usage-tooltip-exempt={"true"}
                                        >
                                            {isCommon ? "Exclude" : "Include"}
                                        </Button>
                                    ) : <Box/>}
                                    <IconButton
                                        aria-label={"Delete WiFi trigger"}
                                        icon={<BiTrash/>}
                                        size={"sm"}
                                        variant={"ghost"}
                                        onClick={() => {
                                            setDraftWiFiEntries((previous) => previous.filter((item) => item.entryId !== entry.entryId));
                                        }}
                                        isDisabled={!canEdit}
                                    />
                                </Grid>
                            );
                        })}
                    </Box>
                    {hasInvalidBeacon ? (
                        <Text mt={3} fontSize={"sm"} color={"red.500"}>
                            One or more beacon addresses are invalid and will not be saved.
                        </Text>
                    ) : null}
                    {activeUsageTooltip && usageTooltipPosition ? (
                        <Box
                            position={"fixed"}
                            left={`${usageTooltipPosition.left}px`}
                            top={`${usageTooltipPosition.top}px`}
                            bg={"gray.700"}
                            color={"white"}
                            padding={3}
                            borderRadius={"md"}
                            boxShadow={"lg"}
                            zIndex={1700}
                            maxWidth={"320px"}
                            pointerEvents={"none"}
                            _before={{
                                content: "\"\"",
                                position: "absolute",
                                left: "-6px",
                                top: `${usageTooltipPosition.arrowTop}px`,
                                width: "0",
                                height: "0",
                                borderTop: "6px solid transparent",
                                borderBottom: "6px solid transparent",
                                borderRight: "6px solid",
                                borderRightColor: "gray.700"
                            }}
                        >
                            <Box maxWidth={"320px"} whiteSpace={"normal"}>
                                {activeUsageTooltip.content}
                            </Box>
                        </Box>
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <Button variant={"ghost"} mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme={"blue"} onClick={save} isDisabled={!canEdit}>Save</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export type {SpotTriggerContext};
export default CueMultiSpotTriggerEditorDialog;
